import { useCallback, useEffect, useState } from "react";
import { contacts as contactsService } from "../../../api/contacts.service";
import type { Entity } from "../../../components/EntityPicker";

/**
 * Manages contact options for a specific office.
 * Contacts are fetched when officeId changes.
 */
export function useContactOptions(initialOfficeId: number | null | undefined) {
  const [selectedOfficeId, setSelectedOfficeId] = useState<number | null>(initialOfficeId ?? null);
  const [contactOptions, setContactOptions] = useState<Entity[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchContacts = useCallback(async (officeId: number) => {
    setIsLoading(true);
    try {
      const response = await contactsService.getAll({ officeWpId: officeId, limit: 100 });
      const options = response.contacts.map(
        (contact: { id: string; contactId: number; name: string; email: string | null }) => ({
          value: contact.contactId,
          label: contact.name,
          description: contact.email ?? "",
        }),
      );
      setContactOptions(options);
    } catch (error) {
      console.error("Failed to fetch contacts:", error);
      setContactOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch contacts for initial office in edit mode
  useEffect(() => {
    if (initialOfficeId) {
      setSelectedOfficeId(initialOfficeId);
      fetchContacts(initialOfficeId);
    }
  }, [initialOfficeId, fetchContacts]);

  const handleOfficeChange = useCallback(
    async (officeId: number | null) => {
      setSelectedOfficeId(officeId);
      if (officeId) {
        await fetchContacts(officeId);
      } else {
        setContactOptions([]);
      }
    },
    [fetchContacts],
  );

  const clearContacts = useCallback(() => {
    setSelectedOfficeId(null);
    setContactOptions([]);
  }, []);

  return {
    selectedOfficeId,
    contactOptions,
    isLoading,
    handleOfficeChange,
    clearContacts,
    fetchContacts,
  };
}
